import React, { PropsWithChildren, useMemo } from 'react';
import { compose } from 'redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { connect } from 'react-redux';
import { BettingHistoryItemLayout } from '@om-fe/widgets/dist/widgets/betting-history/betting-history-item.layout';
import { BettingHistoryItemV2ClassModifiers } from '@om-fe/widgets/dist/widgets/betting-history/betting-history-item/v2/betting-history-item-v2.utils';
import { MatchScoreLayout, MatchScoreScopes } from '@om-fe/widgets/dist/widgets/match-score/match-score.constants';
import { SharedBetDisplay } from '@om-fe/widgets/dist/widgets/betting-history/shared-bet-display/shared-bet-display';
import { useI18n } from '@om-fe/widgets/dist/common/i18n/i18n.hooks';
import { BreakpointsState } from '@om-fe/core/core/store/breakpoints';
import { AppState } from '@om-fe/core/core/types';
import { OddsFormat, PlatformTypes } from '@om-fe/utils';
import getConfig, { DateFormatConfigKey, getConfigDateFormatType, OMFE2ConfigModel, BetslipConfig } from '@om-fe/core/utils/config';

type BettingHistoryPageRouteProps = RouteComponentProps<{ readonly betToken?: string }>;

interface BettingHistoryPageStoreProps {
    breakpoints: BreakpointsState,
    oddsFormat: OddsFormat,
    deviceType?: PlatformTypes;
}

type BettingHistoryPageProps = PropsWithChildren<BettingHistoryPageRouteProps & BettingHistoryPageStoreProps>;

function SharedBetComponent(props: BettingHistoryPageProps) {
    const { gettext } = useI18n();
    const {
        match: { params: { betToken } },
        breakpoints: { small },
        oddsFormat,
        deviceType,
    } = props;
    const config: OMFE2ConfigModel = getConfig();
    const betslip: BetslipConfig | undefined = config?.betslip;
    const classModifiers = useMemo(() => {
        return ['SharedView', small ? BettingHistoryItemV2ClassModifiers.Small : BettingHistoryItemV2ClassModifiers.Big];
    }, [small]);

    const {
        showShareBetButton = false,
        showShareBetButtonDesktop = false,
    } = betslip?.shareBetButton ?? {};

    const {
        desktop: openBetDesktop = false,
        tablet: openBetTablet = false,
        mobile: openBetMobile = false,
    } = betslip?.shareBetButtonOnOpenBets ?? {};

    const {
        desktop: myBetDesktop = false,
        tablet: myBetTablet = false,
        mobile: myBetMobile = false,
    } = betslip?.shareBetButtonOnMyBets ?? {};

    const isDesktop: boolean = deviceType === 'desktop';
    const isTablet: boolean = deviceType === 'tablet';
    const isMobile: boolean = deviceType === 'mobile';

    const showShareBet: boolean = (showShareBetButtonDesktop && isDesktop) || (showShareBetButton && !isDesktop);
    const showShareBetOnOpenBet: boolean = (openBetDesktop && isDesktop) || (openBetTablet && isTablet) || (openBetMobile && isMobile);
    const showShareBetOnMyBet: boolean = (myBetDesktop && isDesktop) || (myBetTablet && isTablet) || (myBetMobile && isMobile);

    return (
        <>
            <h1 className={'PageTitle'}>{gettext('Shared Bet')}</h1>

            <SharedBetDisplay
                itemLayout={BettingHistoryItemLayout.V2}
                classModifiers={classModifiers}
                oddsFormat={oddsFormat}
                sharedBetToken={betToken}
                matchScoreScope={MatchScoreScopes.BHDetailed}
                matchScoreLayout={MatchScoreLayout.V1}
                dateFormat={getConfigDateFormatType(DateFormatConfigKey.FormatFullDate)}
                timeFormat={getConfigDateFormatType(DateFormatConfigKey.FormatHour)}
                hasShareBet={(showShareBet || showShareBetOnOpenBet || showShareBetOnMyBet)}
                isSharedBetPage={true}
            />
        </>
    );
}

export const SharedBetPage = compose(
    withRouter,
    connect<BettingHistoryPageStoreProps, null, {}, AppState>(
        (state: AppState): BettingHistoryPageStoreProps => {
            return {
                breakpoints: state.breakpoints,
                oddsFormat: state.settings.oddsFormat,
                deviceType: state.deviceInfo?.platform.type,
            };
        },
    ),
)(SharedBetComponent);
